import { connect } from 'react-redux';
import React from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import _ from 'lodash';
import StringService from '~/utils/StringService';
import Helpers from '~/utils/Helpers';
import { GLOSSARY } from '~/constants/ModelConstants';
import Tooltip from '~/components/Tooltip';
import ArtifactHelper from '~/utils/ArtifactHelper';
import CvssService from '~/utils/CvssService';
type CSArtifactRowProps = {
  searchResult: object;
  queryWords?: any[];
  isLoggedInAndPaidOrTrialing?: boolean;
  isCvss3?: boolean;
  vcPageState: object;
};
const CSArtifactRow: React.SFC<CSArtifactRowProps> = props => {
  const {
    searchResult,
    isLoggedInAndPaidOrTrialing,
    isCvss3 = false,
    vcPageState: { shouldShowVeracodePage },
  } = props;
  const {
    model: vuln = {},
    _vulnerableMethods = false,
    _cvss2Severity,
    _cvss3Severity,
  } = searchResult;
  const isArtifactPremium = ArtifactHelper.isArtifactPremium(vuln);
  const isArtifactViewable = !isArtifactPremium || isLoggedInAndPaidOrTrialing;
  const vulnTypes = vuln.vulnerabilityTypes;
  const artifactComponents = vuln.artifactComponents;
  const queryWords = props.queryWords || [];
  const libsAffectedCountObj = _(artifactComponents)
    .groupBy('componentId')
    .value();
  const libsAffectedCount = Object.keys(libsAffectedCountObj).length;
  const libsAffectedText = (
    <span>
      {libsAffectedCount > 0 ? (
        <span>
          {libsAffectedCount} librar{libsAffectedCount > 1 ? 'ies' : 'y'} affected{' '}
          <i className="sci sci--fw sci__catalog pr-- va-default color--danger" />
        </span>
      ) : (
        `No libraries affected`
      )}
    </span>
  );
  let vulnURL,
    cveSection = <span>No CVE</span>;
  if (vuln.language && vuln.title && vuln.id) {
    vulnURL = StringService.createSEOVulnItemUrl(vuln.title, vuln.language, vuln.id) + '/summary';
  } else if (vuln.id) {
    vulnURL = 'catalog/vulnerabilities/' + vuln.id;
  } else {
    // we don't have a vulnerability id, which is a problem that should never happen
    vulnURL = '404';
  }
  const cvssVectorLevel = isCvss3
    ? CvssService.getCvss3Severity(_cvss3Severity)
    : CvssService.getCvssSeverity(_cvss2Severity);
  const severityClass = classNames({
    'bg-color--danger--dark': isCvss3 && cvssVectorLevel === 'Critical',
    'bg-color--danger': cvssVectorLevel === 'High',
    'bg-color--warning': cvssVectorLevel === 'Medium',
    'bg-color--warning-light': cvssVectorLevel === 'Low',
  });
  const severityBarBorderClass = classNames({
    'border-color--danger--dark': isCvss3 && cvssVectorLevel === 'Critical',
    'border-color--danger': cvssVectorLevel === 'High',
    'border-color--warning': cvssVectorLevel === 'Medium',
    'border-color--warning-light': cvssVectorLevel === 'Low',
    'bo-v--1': true,
  });
  const severityBarClass = classNames(severityClass, severityBarBorderClass, 'col-1-3 pv--- pl--');
  const vcFontSizeClass = classNames({ 'font--12': shouldShowVeracodePage });
  const severityBoxClass = classNames(
    severityClass,
    'col-1-1 text--center color--white ph-- pv-- font-family--body',
    {
      'font--12': shouldShowVeracodePage,
    }
  );
  const hasExploitIcon = vuln.hasExploits ? (
    <div>
      <span>Has Exploit</span> <i className="inline-block circle- bg-color--danger" />
    </div>
  ) : null;
  const hasVulnMethodIcon = _vulnerableMethods ? (
    <div>
      <span>Vulnerable Method</span>{' '}
      <i className="sci sci__vulnerability pr-- va-default color--danger" />
    </div>
  ) : null;
  let tagEls = vulnTypes.slice(0, 2).map((vulnType, idx) => (
    <span className="ml-- bo--1 border-color--black pv--- ph---" key={'vuln-type-' + idx}>
      {vulnType}
    </span>
  ));
  if (vulnTypes.length === 0) {
    tagEls = 'No tags.';
  }
  //
  if (+vuln.cveYear && +vuln.cveDigits) {
    const artifactCve = vuln.cveYear + '-' + vuln.cveDigits;
    cveSection = (
      <a
        href={'http://web.nvd.nist.gov/view/vuln/detail?vulnId=CVE-' + artifactCve}
        target="_blank"
      >
        CVE-{artifactCve} <i className="sci sci__external-link" />
      </a>
    );
  }
  let descriptionText = 'Description only available to premium users';
  if (isArtifactViewable) {
    descriptionText = vuln.overview ? vuln.overview : 'Description pending';
  }
  const severityBox = (
    <div className={severityBoxClass}>{isCvss3 ? _cvss3Severity : _cvss2Severity}</div>
  );
  let enhancedEl = null;
  if (vuln.teardown) {
    enhancedEl = (
      <div className="col-1-5 pv--- pl-- bo--1 border-color--muted-light color--black-light grid--middle">
        <Tooltip id={`${vuln.id}-enhanced`} width={`width--200`} content={GLOSSARY.ENHANCED}>
          <span className={vcFontSizeClass}>Enhanced</span>
          <i className="sci sci__lightning sci--sm pl- va-default color--warning" />
        </Tooltip>
      </div>
    );
  }
  const premiumIcon = (
    <span className="inline-block">
      <Tooltip id={`${searchResult.id}-${vuln.id}-premium`} content={'Premium Artifact'}>
        <i className="sci sci__crown color--premium font--16 mb--- ml--" />
      </Tooltip>
    </span>
  );
  return (
    <div className="grid pt--">
      <div className="grid__item col-1-1">
        <div className="grid grid--middle ph--">
          <div className="grid__item col-3-5">
            <div
              className={`col-1-1 text--uppercase color--white p- lh grid grid--middle font-family--body ${vcFontSizeClass}`}
            >
              <div className={severityBarClass}>Vulnerability Artifact</div>
              <div
                className={`col-1-3 bg-color--black-dark pv--- pl-- border-color--black bo-v--1 ${vcFontSizeClass}`}
              >
                {'SRCCLR-SID-' + vuln.id}
              </div>
              {enhancedEl}
            </div>
          </div>
        </div>
        <div className="grid">
          <div className="grid__item col-1-1">
            <div className="font--h6 mt--">
              <Link to={vulnURL} className="link--no-underline">
                <span
                  className="max--col-7-10 text--overflow pl0 font--h6"
                  //eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={Helpers.highlightItems(
                    vuln.title || 'Summary pending',
                    queryWords
                  )}
                />
                {!isArtifactViewable && premiumIcon}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div
        className="grid__item col-1-1 mt-- pr-"
        //eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={Helpers.highlightItems(descriptionText, queryWords, 400)}
      />
      <div className="grid__item col-1-1 mt--">
        <div className="grid bg-color--white-medium grid--full grid--middle pv--">
          <div className="col-1-4 grid__item grid grid--middle pl--">
            <div className="grid__item col-1-4">{severityBox}</div>

            <div className="grid__item col-1-2 pl--">{cveSection}</div>
          </div>

          <div className="col-1-5 grid__item grid grid--middle flex">{hasExploitIcon}</div>
          <div className="col-1-5 grid__item grid grid--middle flex">{libsAffectedText}</div>
          <div className="col-1-5 grid__item grid grid--middle">{hasVulnMethodIcon}</div>
          <div className="col-1-5 grid__item flex">
            <i className="sci sci__categories pr-- va-default sci--md-sm" />
            {tagEls}
          </div>
        </div>
      </div>
      <div className="grid__item col-1-1 mt-">
        <div className="bo-b--1 border-color--muted" />
      </div>
    </div>
  );
};
function mapStateToProps(state) {
  return {
    vcPageState: state.vcPageState,
  };
}
export default connect(mapStateToProps)(CSArtifactRow);
